<template>
  <div class="franchise-login-step card-base fade-in">
    <div class="card-body p-4">
      <h1 class="card-title mb-4 text-uppercase">
        <span class="gradient-text">Welcome</span> <span class="text-primary-color">Back</span>
      </h1>

      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-floating mb-3 input-animation">
          <input 
            type="text" 
            class="form-control custom-input" 
            v-model="username" 
            id="usernameInput" 
            placeholder="Username"
            required
          >
          <label for="usernameInput">Email / Username</label>
        </div>

        <div class="form-floating mb-3 input-animation">
          <input 
            :type="passwordFieldType" 
            class="form-control custom-input" 
            v-model="password" 
            id="passwordInput"
            placeholder="Password"
            required
            autocomplete="on"
          >
          <label for="passwordInput">Password</label>
        </div>

        <div class="forgot-password text-start mt-1">
          <a href="#" class="forgot-link" @click.prevent="resetPassword">Forgot Password? Reset</a>
        </div>

        <div class="show-password-container">
          <label class="show-password-checkbox">
            <input type="checkbox" v-model="showPassword">
            <span class="checkbox-text">Show Password</span>
          </label>
        </div>

        <div class="text-center mt-4">
          <p class="text-danger error-message">{{ errorMessage }}</p>
          <button :disabled="loading" type="submit" class="btn-primary-lightning login-button">
            <span class="button-text">Login to Franchise</span>
            <span v-if="loading" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
          </button>
        </div>

        <div class="text-center mt-3">
          <a href="https://wa.me/260958168829" target="_blank" class="contact-sales-link">
            <font-awesome-icon icon="fa-solid fa-comment-dots" class="me-2" />
            Contact sales team to create account
          </a>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { responses } from '@/utils/responses';

export default {
  name: 'FranchiseLoginStep',
  data() {
    return {
      username: '',
      password: '',
      loading: false,
      errorMessage: '',
      showPassword: false
    };
  },
  computed: {
    passwordFieldType() {
      return this.showPassword ? 'text' : 'password';
    }
  },
  methods: {
    async resetPassword() {
      try {
        const { value: email } = await Swal.fire({
          title: 'Reset Password',
          input: 'email',
          inputLabel: 'Enter your email address',
          inputPlaceholder: 'name@example.com',
          showCancelButton: true,
          confirmButtonText: 'Send Reset Link',
          inputValidator: (value) => {
            if (!value) return 'Please enter your email address';
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) return 'Please enter a valid email address';
            return undefined;
          }
        });
        if (!email) return;

        const url = 'https://chrilantech.com/LytSoftPosOnline/api/email-reset-password';
        this.loading = true;
        Swal.showLoading();
        const { status } = await axios.post(url, { email });
        if (status >= 200 && status < 300) {
          responses.showSuccess("We've sent a reset password link to your email.");
          await Swal.fire({ icon: 'success', title: 'Email Sent', text: "If the email exists, a reset link has been sent." });
        } else {
          throw new Error('Failed to send reset request');
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
          responses.throwErr('Please use your company-registered email address.');
        } else {
          if (window?.errorHandler?.tomcatError) window.errorHandler.tomcatError(err, true);
          responses.throwErr('Unable to send reset link at the moment. Please try again later.');
        }
      } finally {
        Swal.hideLoading?.();
        this.loading = false;
      }
    },

    async handleLogin() {
      // this.loading = true;
      this.$utils.showLoading()
      this.errorMessage = '';

      try {
        await this.$store.dispatch('appLogin', {
          email: this.username,
          password: this.password,
        });

        // Emit success to parent
        this.$emit('next', { step: 'franchise-complete' });
      } catch (err) {
        this.errorMessage = err || 'Login failed. Please check your credentials.';
      } finally {
        this.$utils.hideLoading()
        // this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.franchise-login-step {
  padding: 0;
  width: 100%;
  background-color: var(--card-bg);
  border: var(--card-border);
  box-shadow: var(--card-shadow);
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--card-shadow-hover);
  }
}

.card-title {
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 1.8rem;
}

.text-primary-color {
  color: var(--text-primary);
}

.gradient-text {
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.login-form {
  margin-top: 1.5rem;
  opacity: 1; /* Ensure visible without animations for Electron */
}

.input-animation {
  opacity: 1;
  transform: translateY(0);
}

.custom-input {
  border: var(--card-border);
  background-color: transparent;
  color: var(--text-primary);
  transition: all 0.3s ease;
  border-radius: 8px;

  &:focus {
    box-shadow: 0 0 0 2px var(--accent-electric-blue);
    border-color: var(--accent-electric-blue);
  }
}

.forgot-password {
  margin-bottom: 0.5rem;
}

.forgot-link {
  color: var(--accent-electric-blue);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;

  &:hover {
    color: var(--accent-neon-purple);
  }
}

.show-password-container {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1.5rem;
}

.show-password-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.checkbox-text {
  margin-left: 8px;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.login-button {
  width: 100%;
  height: 48px;
  font-size: 1rem;
  letter-spacing: 1px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg, 
      rgba(255, 255, 255, 0) 0%, 
      rgba(255, 255, 255, 0.2) 50%, 
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.6s;
  }

  &:hover::before {
    left: 100%;
  }
}

.error-message {
  min-height: 24px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.contact-sales-link {
  display: inline-flex;
  align-items: center;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.95rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;

  &:hover {
    color: var(--accent-electric-blue);
    background: rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.1);
  }
}

/* Animations removed for compatibility with reduced motion settings */

/* Dark mode specific adjustments */
[data-theme='dark'] .custom-input {
  background-color: rgba(255, 255, 255, 0.05);
}

[data-theme='dark'] .form-floating label {
  color: var(--text-secondary);
}

/* Responsive */
@media (max-width: 768px) {
  .card-title {
    font-size: 1.5rem;
  }

  .card-body {
    padding: 1.5rem !important;
  }
}
</style>
